import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { linkTokenService, getErrorMessage } from '../../../services/apiService';
import {
  STAGE_LABELS,
  STAGE_BADGE_CLASS,
  STATUS_LABELS,
  STATUS_BADGE_CLASS,
  formatDateTime,
  formatRelativeTime,
  formatExpiryCountdown,
  describeEvent,
  groupEvents,
  EVENT_TONE_CLASS,
} from '../utils/linkTokenFormat';

const StageBadge = ({ stage }) => (
  <Badge variant="outline" className={STAGE_BADGE_CLASS[stage] || 'bg-slate-100 text-slate-800'}>
    {STAGE_LABELS[stage] || stage}
  </Badge>
);

const StatusBadge = ({ status }) => (
  <Badge variant="outline" className={STATUS_BADGE_CLASS[status] || 'bg-slate-100 text-slate-800'}>
    {STATUS_LABELS[status] || status}
  </Badge>
);

/**
 * Slide-over drawer answering "the vendor clicked the link and it said invalid".
 * Left: the vendor's full onboarding chain in issued_at order (superseded rows
 * nested under the row that replaced them). Selecting a token loads its
 * append-only event trail on the right.
 *
 * Uses the same overlay shell as the existing modals (fixed inset-0 + backdrop);
 * no drawer primitive exists in the design system, so this is purpose-built but
 * styled entirely with existing tokens.
 */
const JourneyDrawer = ({ vendorId, initialTokenId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedTokenId, setSelectedTokenId] = useState(initialTokenId || null);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);

  // Load the journey. Registry rows can predate a Vendor (e.g. a fresh
  // registration invite has vendor_id null); in that case there is no journey to
  // fetch and we fall back to just the selected token's event trail below.
  useEffect(() => {
    if (vendorId == null) {
      setLoading(false);
      setData(null);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await linkTokenService.getJourney(vendorId);
        if (!active) return;
        setData(res.data);
        // Default selection: the row the user opened from, else the newest.
        if (!initialTokenId && res.data?.tokens?.length) {
          setSelectedTokenId(res.data.tokens[res.data.tokens.length - 1].id);
        }
      } catch (err) {
        if (active) setError(getErrorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [vendorId, initialTokenId]);

  // Load events for the selected token.
  useEffect(() => {
    if (!selectedTokenId) return undefined;
    let active = true;
    (async () => {
      try {
        setEventsLoading(true);
        setEventsError(null);
        const res = await linkTokenService.getEvents(selectedTokenId);
        if (active) setEvents(res.data || []);
      } catch (err) {
        if (active) setEventsError(getErrorMessage(err));
      } finally {
        if (active) setEventsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [selectedTokenId]);

  // ESC to close.
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Lock body scroll while the drawer is open; restore the prior value on close.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Consecutive VIEWED repeats collapse into one line — see groupEvents.
  const groupedEvents = groupEvents(events);

  const tokens = data?.tokens || [];
  // Top-level chain rows = rows that did not themselves supersede an earlier row
  // for this vendor are still shown; we render each row and nest its superseded
  // predecessor beneath it. To avoid rendering a superseded row twice (once as a
  // top-level entry and once nested), only render rows without an active/most-
  // recent successor at top level, and show predecessors inline.
  const supersededIds = new Set(
    tokens.filter((t) => t.superseded_row).map((t) => t.superseded_row.id)
  );

  // Portaled to document.body so the drawer escapes the main-content subtree — the app
  // header (sticky, backdrop-blur -> its own stacking context) and the fixed sidebar
  // (z-50) otherwise trapped/tied the drawer's stacking. z-[100] is the app's real modal
  // layer (above sidebar z-50 and header z-30); note `z-200` used elsewhere is a no-op
  // class that compiles to nothing.
  return createPortal(
    <div className="fixed inset-0 z-[100] flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white shadow-xl border-l border-slate-200 w-full max-w-3xl h-full flex flex-col">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-slate-900">Vendor Journey</h2>
            {data?.vendor ? (
              <p className="text-sm text-slate-600 mt-0.5 truncate">
                {data.vendor.company_name}
                {data.vendor.status && (
                  <span className="ml-2 text-slate-400">({data.vendor.status})</span>
                )}
              </p>
            ) : (
              vendorId == null && (
                <p className="text-sm text-slate-600 mt-0.5">
                  Invited, but hasn&apos;t registered yet — so there is no vendor record to show.
                </p>
              )
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full p-2 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-600 mx-auto"></div>
                <p className="mt-4 text-slate-600">Loading journey...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <Icon name="AlertCircle" size={40} className="mx-auto text-red-500 mb-3" />
              <p className="text-slate-600">{error}</p>
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Chain */}
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Onboarding Steps
                </h3>
                {tokens.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    {vendorId == null
                      ? 'This vendor is still at the invitation step — once they submit the registration form, the rest of their onboarding will appear here.'
                      : 'No links recorded for this vendor.'}
                  </p>
                ) : (
                  <ol className="space-y-2">
                    {tokens
                      .filter((t) => !supersededIds.has(t.id))
                      .map((t) => (
                        <li key={t.id}>
                          <ChainRow
                            row={t}
                            selected={t.id === selectedTokenId}
                            onSelect={() => setSelectedTokenId(t.id)}
                          />
                          {/* Nested predecessor(s) this row replaced */}
                          {t.superseded_row && (
                            <div className="ml-6 mt-2 border-l-2 border-slate-200 pl-4">
                              <PredecessorRow row={t.superseded_row} />
                            </div>
                          )}
                        </li>
                      ))}
                  </ol>
                )}
              </div>

              {/* Selected token event trail */}
              <div>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    What happened to this link
                  </h3>
                </div>
                {eventsLoading ? (
                  <p className="text-sm text-slate-500">Loading history...</p>
                ) : eventsError ? (
                  <p className="text-sm text-red-600">{eventsError}</p>
                ) : events.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    Nothing has happened to this link yet.
                  </p>
                ) : (
                  <ol className="space-y-0">
                    {groupedEvents.map((ev, i) => (
                      <EventRow
                        key={ev.id}
                        event={ev}
                        isLast={i === groupedEvents.length - 1}
                      />
                    ))}
                  </ol>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-end flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * One step of the story, as a sentence rather than an enum + JSON blob. Raw event
 * meta is intentionally not surfaced here — it is internal debugging output.
 */
const EventRow = ({ event, isLast }) => {
  const d = describeEvent(event);
  const tone = EVENT_TONE_CLASS[d.tone] || EVENT_TONE_CLASS.slate;
  return (
    <li className="flex gap-3">
      {/* Rail: icon bubble + connector down to the next event */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className={`h-8 w-8 rounded-full border flex items-center justify-center ${tone.ring}`}
        >
          <Icon name={d.icon} size={15} className={tone.icon} />
        </div>
        {!isLast && <div className="w-px flex-1 bg-slate-200 mt-1" />}
      </div>

      <div className={`min-w-0 flex-1 ${isLast ? '' : 'pb-3'}`}>
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <span className="text-sm font-semibold text-slate-900">{d.title}</span>
          <span className="text-xs text-slate-400 whitespace-nowrap">
            {formatRelativeTime(event.occurred_at)}
          </span>
        </div>
        {d.detail && <p className="text-sm text-slate-600 mt-0.5">{d.detail}</p>}
        <div className="text-xs text-slate-400 mt-1">{formatDateTime(event.occurred_at)}</div>
      </div>
    </li>
  );
};

const ChainRow = ({ row, selected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
      selected ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white hover:bg-slate-50'
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <StageBadge stage={row.stage} />
        <StatusBadge status={row.status} />
        {row.regeneration_count > 0 && (
          <span className="text-xs text-slate-500">
            resent {row.regeneration_count} time{row.regeneration_count === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <Icon name="ChevronRight" size={16} className="text-slate-400" />
    </div>
    <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
      <span>Sent {formatRelativeTime(row.issued_at)}</span>
      {row.first_viewed_at && <span>Opened {formatRelativeTime(row.first_viewed_at)}</span>}
      {row.consumed_at && <span>Completed {formatRelativeTime(row.consumed_at)}</span>}
      <span className="text-slate-400">{formatExpiryCountdown(row.expires_at).text}</span>
    </div>
  </button>
);

const PredecessorRow = ({ row }) => (
  <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2">
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-slate-500">older link, no longer works</span>
      <StageBadge stage={row.stage} />
      <StatusBadge status={row.status} />
    </div>
    <div className="mt-1 text-xs text-slate-400">Sent {formatRelativeTime(row.issued_at)}</div>
  </div>
);

export default JourneyDrawer;
